import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { stringifySqlAst } from '@vendia/management-api-common/src/sql/flora-parser'
import { LakehouseConnectionTypeEnum, LakehouseSyncScheduleEnum, Node, Uni } from '@vendia/management-api-types'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import CopyToClipboardButton from 'src/components/buttons/copy-to-clipboard-button'
import Card from 'src/components/containers/card'
import ListboxField from 'src/components/fields/listbox.field'
import Icon from 'src/components/icons/icon'
import ConfirmationModal from 'src/components/modals/confirmation-modal'
import { Table } from 'src/types/types'
import { useNodeApi } from 'src/utils/api-provider'
import { useGetInitialTableTaskEntities } from 'src/utils/hooks/use-get-table-task-trigger-config'
import { useListVendiaTables } from 'src/utils/hooks/use-list-vendia-tables'
import { getFriendlyScheduleName } from 'src/utils/lakehouse/products'
import { SourceTableInfo } from 'src/utils/lakehouse/tables'
import notify from 'src/utils/notify'

export const ConfigurationCard = ({
  uni,
  nodeName,
  table,
  sourceInfo,
}: {
  uni: Uni
  nodeName: string
  table: Table
  sourceInfo: SourceTableInfo
}) => {
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const nodeApi = useNodeApi()
  const nodes = uni.nodes
  const isOwnedTable = table._owner === nodeName

  const { data: initialTableTaskEntities, refetch: refetchInitialTableTaskEntities } = useGetInitialTableTaskEntities({
    tableTaskDefinitionName: `add_${table.name}`,
    tableId: table._id,
  })
  const syncSchedule: LakehouseSyncScheduleEnum = initialTableTaskEntities?.tableTaskTriggerConfig?.schedule[0]?.cron

  const scheduleForm = useForm({
    defaultValues: {
      syncSchedule: syncSchedule,
    },
  })

  const updateTableTaskTriggerConfigMutation = /* GraphQL */ `
    mutation updateTableTaskTriggerConfig($id: ID!, $input: Vendia_TableTaskTriggerConfig_UpdateInput_!) {
      updateVendia_TableTaskTriggerConfig(id: $id, input: $input, syncMode: NODE_LEDGERED) {
        transaction {
          _id
          _owner
          submissionTime
          transactionId
          version
        }
      }
    }
  `

  const updateLakehouseScheduleMutation = useMutation({
    mutationFn: async ({
      triggerConfigId,
      taskId,
      syncSchedule,
    }: {
      triggerConfigId: string
      taskId: string
      syncSchedule: LakehouseSyncScheduleEnum
    }) => {
      const result = await nodeApi?.urqlClient
        ?.mutation(updateTableTaskTriggerConfigMutation, {
          id: triggerConfigId,
          input: {
            schedule: {
              cron: syncSchedule,
              taskId: taskId,
            },
          },
        })
        .toPromise()

      if (result?.error) {
        throw new Error(result.error.message)
      }

      return result?.data
    },

    onError: (error) => notify.error(`Error updating refresh rate: ${error}`),
    onSuccess: (response) => {
      if (!response?.updateVendia_TableTaskTriggerConfig) {
        notify.error(`Error updating refresh rate for: ${table.name}`)
        return
      }

      notify.success(`Successfully updated refresh rate for: ${table.name}`)
      setShowScheduleModal(false)
      refetchInitialTableTaskEntities()
    },
  })

  return (
    <>
      <Card className='w-full' overflow='visible'>
        <div className='grid space-y-6'>
          <TableSource table={table} isOwnedTable={isOwnedTable} sourceInfo={sourceInfo} />
          <TableAcls table={table} nodeName={nodeName} nodes={nodes!} />
          <div className='flex flex-col justify-between'>
            <h5 className='font-bold'>
              Frequency
              {isOwnedTable && (
                <Icon name='pencil' size={16} className='text-link ml-1' onClick={() => setShowScheduleModal(true)} />
              )}
            </h5>
            <div>{getFriendlyScheduleName[syncSchedule]?.toLowerCase()}</div>
          </div>
          {sourceInfo.lastUpdatedColumn && (
            <div>
              <h5 className='font-bold'>Timestamp Column</h5>
              <div className='text-neutral-8 text-sm'>{sourceInfo.lastUpdatedColumn}</div>
            </div>
          )}
          {sourceInfo.keyColumns?.[0] !== '' && (
            <div>
              <h5 className='font-bold'>Key Columns</h5>
              <div className='text-neutral-8 text-sm'>{sourceInfo.keyColumns?.join(', ')}</div>
            </div>
          )}
          <JoinQuery table={table} />
        </div>
      </Card>
      <ConfirmationModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        title={`Syncing schedule`}
        onSubmit={() => {
          if (scheduleForm.state.values.syncSchedule === table.syncSchedule) {
            notify.error(
              `${table.name} is already set to refresh: ${getFriendlyScheduleName[table.syncSchedule]?.toLowerCase()}`,
            )
            return
          }

          if (
            !initialTableTaskEntities?.tableTaskTriggerConfig?._id ||
            !initialTableTaskEntities?.taskDefinition?._id
          ) {
            notify.error('Unable to update schedule')
            return
          }

          updateLakehouseScheduleMutation.mutate({
            triggerConfigId: initialTableTaskEntities.tableTaskTriggerConfig._id,
            taskId: initialTableTaskEntities.taskDefinition._id,
            syncSchedule: scheduleForm.state.values.syncSchedule,
          })
        }}
        isSubmitting={updateLakehouseScheduleMutation.isPending}
        actionButtonType={`primary`}
        actionButtonText={`Update`}
        confirmationText={'Update'}
      >
        <ListboxField
          name='syncSchedule'
          label='Frequency (Refresh rate)'
          description='How often should this table be updated?'
          options={[
            {
              value: LakehouseSyncScheduleEnum.OnDemand,
              label: 'On-demand',
              disabled: syncSchedule === LakehouseSyncScheduleEnum.OnDemand,
            },
            {
              value: LakehouseSyncScheduleEnum.Daily,
              label: 'Daily',
              disabled: syncSchedule === LakehouseSyncScheduleEnum.Daily,
            },
            {
              value: LakehouseSyncScheduleEnum.Weekly,
              label: 'Weekly',
              disabled: syncSchedule === LakehouseSyncScheduleEnum.Weekly,
            },
            {
              value: LakehouseSyncScheduleEnum.Monthly,
              label: 'Monthly',
              disabled: syncSchedule === LakehouseSyncScheduleEnum.Monthly,
            },
          ]}
          form={scheduleForm}
        />
      </ConfirmationModal>
    </>
  )
}

const TableSource = ({
  table,
  isOwnedTable,
  sourceInfo,
}: {
  table: Table
  isOwnedTable: boolean
  sourceInfo: SourceTableInfo
}) => {
  const VendiaSource = () => {
    const vendiaTableNames: string[] = []
    if (sourceInfo?.tableName) {
      const tableName = sourceInfo.tableName.split('.')[1] || sourceInfo.tableName
      vendiaTableNames.push(tableName)
    }
    if (sourceInfo?.joinQuery?.from) {
      sourceInfo.joinQuery.from.forEach((joinQuery: any) => {
        vendiaTableNames.push(joinQuery.table)
      })
    }
    const orFilter = vendiaTableNames?.map((name) => ({ name: { eq: name } }))
    // We need to fetch Vendia tables to find the _id(s) of the source table(s)
    const { data: vendiaTables } = useListVendiaTables({
      _or: orFilter,
    })
    return (
      <div className='flex flex-col gap-2'>
        {vendiaTables?.map((vendiaSourceTable: Table) => (
          <div key={vendiaSourceTable._id} className='flex items-center gap-2'>
            <img src='/images/vendor/vendia-secondary.png' alt='Vendia logo' className='size-4' />
            <Link to={`../tables/${vendiaSourceTable?._id}`} className='truncate' title={vendiaSourceTable?.name}>
              {vendiaSourceTable?.name}
            </Link>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-4'>
      <h5 className='font-bold'>
        {sourceInfo?.joinQuery && sourceInfo.joinQuery.from.length > 1 ? (
          <span>Source Tables</span>
        ) : sourceInfo.fileName ? (
          <span>Source File</span>
        ) : (
          <span>Source Table</span>
        )}
      </h5>
      {!isOwnedTable ? (
        <div className='flex items-center gap-2'>
          <img src='/images/vendor/vendia-secondary.png' alt='Vendia logo' className='size-4' />
          <div className='text-light text-sm'>{table.name}</div>
        </div>
      ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Snowflake ? (
        <div className='flex items-center gap-2 truncate'>
          <img src='/images/vendor/snowflake-secondary.png' alt='Snowflake logo' className='size-4' />
          <div className='text-light text-sm'>{sourceInfo.tableName}</div>
        </div>
      ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Cloudera ? (
        <div className='flex items-center gap-2'>
          <img src='/images/vendor/cloudera-secondary.png' alt='Cloudera logo' className='size-4' />
          <div className='text-light text-sm'>{sourceInfo.tableName}</div>
        </div>
      ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Postgres ? (
        <div className='flex items-center gap-2'>
          <img src='/images/vendor/postgres-secondary.png' alt='Postgres logo' className='size-4' />
          <div className='text-light text-sm'>{sourceInfo.tableName}</div>
        </div>
      ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Databricks ? (
        <div className='flex items-center gap-2'>
          <img src='/images/vendor/databricks-secondary.png' alt='Databricks logo' className='size-4' />
          <div className='text-light text-sm'>{sourceInfo.tableName}</div>
        </div>
      ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Mysql ? (
        <div className='flex items-center gap-2'>
          <img src='/images/vendor/mysql-secondary.png' alt='MySQL logo' className='size-4' />
          <div className='text-light text-sm'>{sourceInfo.tableName}</div>
        </div>
      ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Bigquery ? (
        <div className='flex items-center gap-2'>
          <img src='/images/vendor/bigquery-secondary.png' alt='BigQuery logo' className='size-4' />
          <div className='text-light text-sm'>{sourceInfo.tableName}</div>
        </div>
      ) : table.sourceConnectionType === LakehouseConnectionTypeEnum.Vendia && !table.joinQuery ? (
        <div className='flex items-center gap-2'>
          <img src='/images/vendor/vendia-secondary.png' alt='Vendia logo' className='size-4' />
          <Link
            to={`../tables/${sourceInfo.tableName?.split('.')[1] || sourceInfo.tableName}`}
            className={'text-light text-xs'}
            title={sourceInfo.tableName?.split('.')[1]}
          >
            {sourceInfo.tableName?.split('.')[1] || sourceInfo.tableName}
          </Link>
        </div>
      ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Vendia ? (
        <VendiaSource />
      ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.Upload ? (
        <div className='flex items-center gap-2'>
          <img src='/images/vendor/upload-secondary.png' alt='Upload file logo' className='size-4' />
          <div className='text-light text-sm'>{sourceInfo.fileName}</div>
        </div>
      ) : sourceInfo.connectionType === LakehouseConnectionTypeEnum.S3 ? (
        <div className='flex items-center gap-2'>
          <img src='/images/vendor/s3-secondary.png' alt='S3 file logo' className='size-4' />
          <div className='text-light text-sm'>
            <div className='text-neutral-8 max-w-[300px] truncate text-xs' title={sourceInfo.fileName}>
              {sourceInfo.fileName?.split('/').pop()}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

const TableAcls = ({ table, nodeName, nodes }: { table: Table; nodeName: string; nodes: Node[] }) => {
  if (!table._acl) return null
  if (nodes.length === 1) return null // Single node Uni means no sharing
  if (table._owner !== nodeName) return null // Only show Shared With if the current node is the table owner
  if (table._acl.length === 0) return null // Empty ACL = not shared

  // Filter out ACLs that only grant access to the nodeTableName, the natural key
  const acls = table._acl.filter((aclEntry) => aclEntry.path !== 'nodeTableName')

  if (acls.length === 0) return null

  // Check for ACL with wildcard (*) principal.nodes and null path - means shared with all nodes
  const hasWildcardAccess = acls.some((acl) => acl.principal?.nodes?.includes('*') && acl.path === null)

  if (hasWildcardAccess) {
    return (
      <div>
        <h5 className='font-bold'>Shared With</h5>
        {nodes
          ?.filter((node) => node.name !== nodeName) // Filter out the owner node
          .map((node) => (
            <div key={node.name} className='flex items-center gap-2'>
              {node.name}
            </div>
          ))}
      </div>
    )
  }

  return (
    <div>
      <h5 className='font-bold'>Shared With</h5>
      {nodes
        ?.filter((node) => {
          return acls.some((acl) => acl.principal?.nodes?.includes(node.name))
        })
        .map((node) => (
          <div key={node.name} className='flex items-center gap-2'>
            {node.name}
          </div>
        ))}
    </div>
  )
}

const JoinQuery = ({ table }: { table: Table }) => {
  if (!table.joinQuery) return null

  const { joinQuery } = table
  const prettifiedJoinQuery = stringifySqlAst(joinQuery)
    .replace(/"/g, '')
    // Breaks after each comma in the SELECT part (before FROM)
    .replace(/SELECT (.*?)FROM/gis, (match, selectPart) => {
      return `SELECT ${selectPart.replace(/,\s*/g, ',\n  ')}FROM`
    })
    // Line breaks + indents
    .replace(/; /g, ';\n')
    .replace(/ FROM /gi, '\nFROM ')
    .replace(/ JOIN /gi, '\nJOIN ')
    .replace(/ WHERE /gi, '\nWHERE ')
    .replace(/ AND /gi, '\n  AND ')
    .replace(/ OR /gi, '\n  OR ')
    .replace(/ GROUP BY /gi, '\nGROUP BY ')
    .replace(/ ORDER BY /gi, '\nORDER BY ')
    .replace(/ LIMIT /gi, '\nLIMIT ')

  // Only show the first 5 lines
  const [isExpanded, setIsExpanded] = useState(false)
  const totalLines = prettifiedJoinQuery.split('\n')
  const displayLines = isExpanded ? totalLines : totalLines.slice(0, 5)
  const hasMoreLines = totalLines.length > 5

  return (
    <div className='overflow-auto'>
      <div className='mb-2 flex max-w-full items-start justify-between'>
        <h5 className='font-bold'>
          Join SQL <CopyToClipboardButton iconSize={16} text={prettifiedJoinQuery} />
        </h5>
      </div>
      <div className='my-1 max-w-full rounded-md bg-gray-100 p-4'>
        <pre className='overflow-auto whitespace-pre text-xs text-gray-800'>
          <code>{displayLines.join('\n')}</code>
          {hasMoreLines && !isExpanded && <div className='mt-2 text-gray-500'>...</div>}
        </pre>
        {hasMoreLines && (
          <button onClick={() => setIsExpanded(!isExpanded)} className='text-link mt-2 text-xs'>
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  )
}
